import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Context
import { ModalContext } from '../../context/ModalContext'

// Internal components
import FormDialog from '../FormDialog/FormDialog'
import FormError from '../FormError/FormError'
import { UserContext } from '../../context/UserContext'

const emptyClass = ' not-empty'
const revealClass = ' is-revealed'
const activeClass = ' is-active'

const requiredHint = ' *'

const FormItemSelect = (props) => {
  const {
    item,
    onFormItemChange,
    onFormItemLoad,
    groupedState,
    revealState,
    filteredState,
    enabledState,
    shouldValidateState
  } = props

  const userContext = useContext(UserContext)
  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // Context
  const { register, watch, errors, formState, trigger, setValue } = useFormContext()
  const modalContext = useContext(ModalContext)

  // State
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isDisabled, setIsDisabled] = useState(!item?.disabled)
  const [filteredOptions, setFilteredOptions] = useState(false)
  const [dialogEvent, setDialogEvent] = useState(false)
  const [previousValue, setPreviousValue] = useState(false)
  const [selectHint, setSelectHint] = useState(false)

  const itemProps = item
  const itemRef = useRef()
  const itemValue = watch(item.name)
  const itemInputRef = register(item.name, isRequired)

  useEffect(() => {
    if (!item.revealed && item.required) setIsRequired({ required: requiredErrorMessage })
  }, [])

  useEffect(() => {
    if (!item.revealed || !item.required) return false
    setIsRequired({ required: revealState ? requiredErrorMessage : false })
    trigger()
  }, [revealState])

  useEffect(() => {
    if (!shouldValidateState) setIsRequired({ required: false })
  }, [shouldValidateState])

  useEffect(() => {
    if (!itemValue) return false
    trigger()
    if (!isLoaded && onFormItemLoad) onFormItemLoad({ itemProps, itemRef, itemValue })
    else if (onFormItemChange) onFormItemChange({ itemProps, itemRef, itemValue })
    if (item.hint) setSelectHint(item.options.filter((option) => option.value === itemValue && option.hint))
  }, [itemValue])

  useEffect(() => {
    if (!item?.group || !groupedState) return false
    const itemLabel = item?.group?.label
    const groupedItem = groupedState.filter((str) => {
      return str.label.indexOf(itemLabel) >= 0
    })
    setIsGrouped(groupedItem[0].active)
  }, [groupedState])

  useEffect(() => {
    if (!filteredState) return false
    if (filteredOptions !== filteredState) {
      setFilteredOptions(filteredState)
      trigger()
    }
    setIsLoaded(true)
  }, [filteredState])

  useEffect(() => {
    if (filteredOptions && itemValue) setTimeout(() => setValue(item.name, itemValue), 10)
  }, [filteredOptions])

  useEffect(() => {
    const enables = enabledState?.filter((input) => input.name === item.name)
    const disabled = enables?.[0]?.name === item.name ? !enables?.[0]?.enabled : false
    setIsDisabled(disabled)
    if (enables?.[0]?.toEmpty && isLoaded && itemValue !== '' && itemValue !== null && itemValue !== undefined) {
      setTimeout(() => setValue(item.name, null), 10)
    }
  }, [enabledState])

  useEffect(() => {
    if (isDisabled && (itemValue === '' || itemValue === null || itemValue === undefined))
      setTimeout(() => setValue(item.name, null), 1)
  }, [isDisabled])

  useEffect(() => {
    if (!item.dialog) return false
    modalContext.setModalDialog(dialogEvent)
  }, [dialogEvent])

  const onSelectChange = (e) => {
    if (item.dialog) {
      setPreviousValue(itemValue)
      setDialogEvent(e)
    } else {
      itemInputRef.onChange(e)
    }
  }

  const onFormDialogConfirm = () => {
    itemInputRef.onChange(dialogEvent)
    setDialogEvent(false)
  }

  const onFormDialogDecline = () => {
    setValue(item.name, previousValue)
    setDialogEvent(false)
  }

  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        itemValue ? emptyClass : ''
      }${revealState ? revealClass : ''}${isDisabled ? ' is-disabled' : ''}${dialogEvent ? ' has-dialog' : ''}${
        !isGrouped ? '' : activeClass
      }`}
      data-reveal-target={item.revealed}
    >
      <label className="FormItem-label" htmlFor={item.name}>
        {item.transl ? t(item.transl) : item.label}
        {item.required ? requiredHint : ''}
      </label>
      <select
        id={item.name}
        name={item.name}
        className={`FormItem-input FormItem-input--${item.type}`}
        defaultValue={item.value || ''}
        onChange={(e) => {
          onSelectChange(e)
        }}
        ref={itemInputRef.ref}
      >
        {filteredOptions &&
          item.options.length &&
          filteredOptions.map((option, index) => {
            return (
              <option value={option.value} disabled={option.disabled} key={`FormItem-option--${index + 1}`}>
                {option.transl ? t(option.transl) : option.label}
                {option.suffix ? ` ${t(option.suffix)}` : ''}
              </option>
            )
          })}
        {!filteredOptions &&
          item.options.length &&
          item.options.map((option, index) => {
            return (
              <option value={option.value} disabled={option.disabled} key={`FormItem-option--${index + 1}`}>
                {option.transl ? t(option.transl) : option.label}
                {option.suffix ? ` ${t(option.suffix)}` : ''}
              </option>
            )
          })}
      </select>
      {selectHint?.[0] && itemValue && (
        <div className="FormItem-hint">
          <p>{selectHint?.[0]?.hint}</p>
        </div>
      )}
      {dialogEvent && (
        <FormDialog
          dialogEvent={dialogEvent}
          item={item}
          enabledState={enabledState}
          onFormDialogConfirm={onFormDialogConfirm}
          onFormDialogDecline={onFormDialogDecline}
        />
      )}
      {formState.isSubmitted && !isDisabled && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </div>
  )
}

FormItemSelect.propTypes = {
  item: PropTypes.object,
  onFormItemChange: PropTypes.func,
  onFormItemLoad: PropTypes.func,
  revealState: PropTypes.any,
  filteredState: PropTypes.any,
  enabledState: PropTypes.any,
  groupedState: PropTypes.any,
  shouldValidateState: PropTypes.bool
}

export default FormItemSelect
